import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import StatusBadge from '../components/StatusBadge';
import { Loading, ErrorState, EmptyState } from '../components/States';
import Icon from '../components/Icon';
import { ICONS } from '../lib/icons';

const PROVIDER_ID = 'oci';

// Vaults is the Key Management (KMS) console: it lists the provider's vaults and
// provisions new ones. It mirrors the service-console layout (own left menu via
// useSideNav) used by ServiceDetail.
export default function Vaults() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { setNav } = useSideNav();

  const { data, loading, error, reload } = useAsync(
    () => api.getVaults(PROVIDER_ID),
    []
  );

  // Publish the service-specific left menu while this page is mounted.
  useEffect(() => {
    setNav({
      title: 'Vault',
      subtitle: 'Key Management',
      backTo: '/services',
      items: [
        { label: 'Vaults', to: '/vaults', icon: ICONS.list },
        { label: 'Create vault', to: '/vaults?action=create', icon: ICONS.create },
      ],
    });
  }, [setNav]);
  useEffect(() => () => setNav(null), [setNav]);

  const showCreate = params.get('action') === 'create';
  const openCreate = () => setParams({ action: 'create' });
  const closeCreate = () => {
    const next = new URLSearchParams(params);
    next.delete('action');
    setParams(next, { replace: true });
  };

  if (loading) return <Loading label="Loading vaults…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const vaults = data || [];

  return (
    <div className="stack">
      <PageHeader
        title="Vault"
        subtitle="OCI · Key Management"
        actions={
          <button className="btn btn--primary" onClick={openCreate}>
            + Create vault
          </button>
        }
      />

      <Card title={`Vaults (${vaults.length})`}>
        {vaults.length === 0 ? (
          <EmptyState
            title="No vaults yet"
            detail="Create your first vault to hold encryption keys."
          />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>OCID</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {vaults.map((v) => (
                  <tr key={v.id}>
                    <td className="cell-primary">{v.displayName}</td>
                    <td className="cell-sub">{v.vaultType}</td>
                    <td>
                      <StatusBadge status={v.state} />
                    </td>
                    <td className="cell-sub mono">{v.id}</td>
                    <td className="num">
                      <button
                        className="btn btn--ghost btn--sm"
                        // Keys live under a vault; only meaningful once ACTIVE.
                        disabled={v.state !== 'ACTIVE'}
                        title={
                          v.state === 'ACTIVE'
                            ? 'Manage keys'
                            : 'Available once the vault is ACTIVE'
                        }
                        onClick={() => navigate(`/vaults/${encodeURIComponent(v.id)}`)}
                      >
                        Keys <Icon name={ICONS.arrowRight} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {showCreate && (
        <CreateVaultDialog
          onClose={closeCreate}
          onCreated={() => {
            closeCreate();
            reload();
          }}
        />
      )}
    </div>
  );
}

// CreateVaultDialog is the modal form for provisioning a new vault. Creation is
// asynchronous on OCI: the vault returns in the CREATING state and becomes
// ACTIVE a minute or two later.
function CreateVaultDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ displayName: '', vaultType: 'DEFAULT' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api.createVault(PROVIDER_ID, form);
      onCreated();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div className="modal" role="dialog" aria-modal="true">
      <div className="modal__backdrop" onClick={onClose} />
      <div className="modal__panel">
        <header className="modal__header">
          <h2>Create vault</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">
            <Icon name={ICONS.close} />
          </button>
        </header>

        <form className="modal__body form" onSubmit={submit}>
          <label className="field">
            <span>Display name</span>
            <input
              className="input"
              placeholder="e.g. prod-secrets"
              value={form.displayName}
              onChange={update('displayName')}
              required
            />
          </label>

          <label className="field">
            <span>Vault type</span>
            <select
              className="input"
              value={form.vaultType}
              onChange={update('vaultType')}
            >
              <option value="DEFAULT">Default (shared HSM)</option>
              <option value="VIRTUAL_PRIVATE">Virtual private (dedicated HSM)</option>
            </select>
          </label>

          {error ? <div className="form-error">{error}</div> : null}

          <footer className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn btn--primary" disabled={submitting}>
              {submitting ? 'Creating…' : 'Create vault'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}
